/**
 * On-pitch indicator subsystem (§4.8, Phase 3C — playtest defects #13/#14;
 * pass-target smoothing added LIVE FOOTBALL POLISH §2/§5).
 *
 *   - CONTROLLED RING: a persistent ring under the player you are driving,
 *     pulsing gently, position-fed through RenderState (controlled*) so this
 *     subsystem derives nothing about which slot it is.
 *   - PASS-TARGET DISC: a bright pulsing ring under the teammate a charging
 *     pass would go to (passTarget*) — fed by the game loop from the SAME
 *     shared choosePassTarget() the sim strikes with. Its position is SMOOTHED
 *     (a short lerp) so a candidate change under a sweeping aim eases rather
 *     than pops; visibility itself is never smoothed — it hides the instant the
 *     pass releases (passTargetShow flips false that same frame).
 *
 * That is the whole file. Two rings, no arrows.
 *
 * ── NO ARROWS AT ALL (owner, 2026-07-25) ───────────────────────────────────
 *
 * Verbatim: *"keep the cross air always on the screen and lets use the cross to
 * decide where the ball will go instead of the arrow near his foot. and dont
 * show the arrows near his foot"*.
 *
 * So the aiming display moved OFF the pitch and onto the screen-centre reticle
 * in `ui/hud.ts` (four ticks + a ball-destination dot, geometry in
 * `ui/crosshair-geometry.ts`), and this file lost BOTH of its aim markers:
 *
 *   - the AIM ARROW, a charge-scaled ground cone at the boots along `aimYaw`;
 *   - the world-space AIM CROSSHAIR, a "+" of flat bars `CROSSHAIR_AHEAD_DIST`
 *     (7 m) out along the same yaw.
 *
 * The 7 m marker went with the arrow rather than surviving as a landing-spot
 * indicator, and that is the one judgement call worth writing down: **7 m was a
 * CONSTANT**. It never read the charge, the loft or the power, so it did not
 * mark where the ball would land — a full-power shot leaves the boot and travels
 * thirty-odd metres past it. It was a second drawing of the same DIRECTION the
 * reticle now draws, at an arbitrary distance, on the grass the owner asked to
 * clear. A real landing-spot predictor would need the sim's own ballistics
 * (§3.2 drag + Magnus, and the strike error the sim rolls at release, which is
 * not knowable before the strike) — that is a feature, not a rename of this
 * one, so it is gone rather than left behind lying about its own meaning.
 *
 * Earlier today the same pass removed a FACING CHEVRON at the ring's leading
 * edge (it was gated only on WHO you drive, so it stayed lit with no ball) and
 * the CURVE SHAFT polyline (a quadratic-bezier from boot to arrow head, bent by
 * the live `aimArrowCurl`). `indicators.test.ts` pins the count at ZERO cones
 * and ZERO polylines so no third marker can quietly come back.
 *
 * `RenderState.aimArrow*` and `Renderer.setAimArrow()` still exist but are now
 * written by nobody and read by nobody — deliberately left alone here because
 * `render/context.ts` / `render/renderer.ts` are another agent's files this
 * session; they are dead weight to sweep, not behaviour.
 *
 * What is left under the boots is therefore the controlled RING, which is a
 * WHO-am-I-driving highlight rather than an arrow, and — only while a pass
 * charges, and only under the RECEIVER, never the striker — the pass-target
 * ring.
 *
 * Presentation only: reads RenderState, writes nothing back, allocates at init
 * (§15.7 — zero allocation per frame). All meshes sit just above the grass
 * with depthWrite off so they never z-fight or occlude the ball.
 */
import {
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  Scene,
} from 'three';
import type { RenderContext, RenderState, RenderSubsystem } from './context.js';

/** Ring inner/outer radii — hugs a 0.3 m capsule with room for the pulse. */
const RING_INNER = 0.42;
const RING_OUTER = 0.55;
/** Pass-target disc radius — reads as "the ball is coming HERE". */
const DISC_RADIUS = 0.7;
/** Bold, pulsing highlight so the intended receiver is unmistakable (live
 *  feedback "highlight the teammates so it reaches him correct"): a bright ring
 *  under his feet that breathes. Was a faint 20% white filled circle. */
const DISC_OPACITY = 0.85;
const DISC_COLOR = 0x39ff9a; // bright pitch-green-safe cyan-green
/** Pulse depth + rate (Hz) so it reads as an active "incoming" marker. */
const DISC_PULSE_DEPTH = 0.22;
const DISC_PULSE_HZ = 2.4;
/** Lift above the grass to clear the pitch decals without visible floating. */
const LIFT_Y = 0.03;
/** Pulse: subtle ±8% scale at ~1.6 Hz. */
const PULSE_AMPLITUDE = 0.08;
const PULSE_HZ = 1.6;
/** Pass-target disc: time constant (s) for the candidate-change position ease.
 *  Short on purpose — long enough to read as a glide, short enough that it
 *  never looks like it is lagging the real target. */
const PASS_TARGET_LERP_TIME_S = 0.12;

export function createIndicatorSystem(): RenderSubsystem {
  let ring: Mesh | null = null;
  let disc: Mesh | null = null;
  let sceneRef: Scene | null = null;

  // Pass-target disc smoothing state (module-closure, not per-frame allocation).
  let discWasVisible = false;
  /** Which player the ring was on last frame — a CHANGE snaps it (see below). */
  let discTargetId = -1;
  let discSmoothX = 0;
  let discSmoothY = 0;
  let discSmoothZ = 0;

  return {
    name: 'indicators',

    init(ctx: RenderContext): void {
      sceneRef = ctx.scene;

      const ringMat = new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        side: DoubleSide,
      });
      ring = new Mesh(new RingGeometry(RING_INNER, RING_OUTER, 48), ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 2; // over the pitch decals, under nothing that matters
      ring.visible = false;
      ring.frustumCulled = false; // tiny + always near the action; skip the test
      ctx.scene.add(ring);

      const discMat = new MeshBasicMaterial({
        color: DISC_COLOR,
        transparent: true,
        opacity: DISC_OPACITY,
        depthWrite: false,
        side: DoubleSide,
      });
      // A RING (not a filled disc) reads as a target reticle, not a shadow —
      // the receiver's feet stay visible inside it.
      disc = new Mesh(new RingGeometry(DISC_RADIUS * 0.66, DISC_RADIUS, 40), discMat);
      disc.rotation.x = -Math.PI / 2;
      disc.renderOrder = 2;
      disc.visible = false;
      disc.frustumCulled = false;
      ctx.scene.add(disc);
    },

    update(state: RenderState): void {
      if (ring !== null) {
        ring.visible = state.controlledShow;
        if (state.controlledShow) {
          ring.position.set(state.controlledX, state.controlledY + LIFT_Y, state.controlledZ);
          const pulse =
            1 + PULSE_AMPLITUDE * Math.sin(state.controlledPulse * PULSE_HZ * Math.PI * 2);
          ring.scale.set(pulse, pulse, 1);
          // Switch-blend fades the ring in as control settles on the new man.
          (ring.material as MeshBasicMaterial).opacity = 0.85 * Math.max(0.25, state.controlledBlend);
        }
      }
      if (disc !== null) {
        disc.visible = state.passTargetShow;
        if (state.passTargetShow) {
          if (!discWasVisible || state.passTargetId !== discTargetId) {
            // Snap, don't slide. Two cases:
            //   1. it just appeared (or reappeared after a release);
            //   2. THE RECEIVER CHANGED.
            //
            // Case 2 is the one that mattered. Review: "when passing show the
            // assist or high light mark in milli sec". Easing across a change of
            // receiver dragged the ring between two players over
            // PASS_TARGET_LERP_TIME_S — 120 ms during which the marker sits on
            // GRASS, pointing at nobody, while the pass it represents would go to
            // a man it has not reached yet. That is not a smoothing artefact, it
            // is the indicator lying about its own answer. Switching receiver is a
            // discrete decision and must read as one.
            //
            // The ease is kept for the case it was actually written for: the SAME
            // receiver moving, where the ring should track his run steadily
            // instead of jittering on his per-tick position.
            discSmoothX = state.passTargetX;
            discSmoothY = state.passTargetY;
            discSmoothZ = state.passTargetZ;
          } else {
            // The candidate under the aim may have changed — ease toward the
            // new spot instead of popping. Linear-toward-target ease against a
            // time constant (same idiom InputManager.decayCurl uses), so it is
            // frame-rate independent without needing an allocation.
            const k = state.dt <= 0 ? 1 : Math.min(1, state.dt / PASS_TARGET_LERP_TIME_S);
            discSmoothX += (state.passTargetX - discSmoothX) * k;
            discSmoothY += (state.passTargetY - discSmoothY) * k;
            discSmoothZ += (state.passTargetZ - discSmoothZ) * k;
          }
          disc.position.set(discSmoothX, discSmoothY + LIFT_Y, discSmoothZ);
          // Breathe: a gentle scale pulse so it reads as a live "incoming"
          // marker rather than a static decal.
          const pulse = 1 + DISC_PULSE_DEPTH * Math.sin(state.elapsed * DISC_PULSE_HZ * Math.PI * 2);
          disc.scale.set(pulse, pulse, pulse);
        }
        // Visibility itself is never smoothed: hides the instant the pass
        // releases, whatever the eased position was doing.
        discWasVisible = state.passTargetShow;
        discTargetId = state.passTargetId;
      }
    },

    dispose(): void {
      if (ring !== null) {
        sceneRef?.remove(ring);
        ring.geometry.dispose();
        (ring.material as MeshBasicMaterial).dispose();
        ring = null;
      }
      if (disc !== null) {
        sceneRef?.remove(disc);
        disc.geometry.dispose();
        (disc.material as MeshBasicMaterial).dispose();
        disc = null;
      }
      sceneRef = null;
    },
  };
}
